import { css, Interpolation, Theme } from '@emotion/react';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ChecklistField, Chip, SearchField, useFormField } from '@frontend/design-system';
import { FilterableCheckboxListItem, useFilterableCheckboxList } from './use-filterable-checkbox-list';

interface Props<T extends FilterableCheckboxListItem = FilterableCheckboxListItem> {
  fieldName: string;
  list?: T[];
  checkListField: ReturnType<typeof useFormField<'checklist'>>;
  clearButtonTrackingId?: string;
  selectAllButtonTrackingId?: string;
  listStyle?: Interpolation<Theme>;
  Label?: React.ComponentType<{ item: T }>;
}

export const FilterableCheckboxList = <T extends FilterableCheckboxListItem = FilterableCheckboxListItem>({
  fieldName,
  list,
  checkListField,
  clearButtonTrackingId,
  selectAllButtonTrackingId,
  listStyle,
  Label,
}: Props<T>) => {
  const { t } = useTranslation('schedule');
  const { filteredList, searchField, isAllSelected, isAnySelected, handleSelectAll, handleClear } =
    useFilterableCheckboxList<T>({
      list,
      selectedValues: checkListField.value,
      onChange: (value) => {
        checkListField.onChange({ value, name: fieldName });
      },
    });

  return (
    <section>
      <SearchField
        name='search'
        {...searchField}
        // NOTE: keep this to prevent the popover from not allow typing in the search field
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      />
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant='tertiary'
          disabled={isAllSelected}
          onClick={handleSelectAll}
          trackingId={selectAllButtonTrackingId}
        >
          {t('Select All')}
        </Button>
        <Button variant='tertiary' disabled={!isAnySelected} onClick={handleClear} trackingId={clearButtonTrackingId}>
          {t('Clear')}
        </Button>
      </div>
      {!filteredList?.length ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'schedule',
            header: t('No results found'),
            description: '',
          }}
        />
      ) : (
        <ChecklistField name={fieldName} label={''} {...checkListField} showLabel={false} css={listStyle}>
          {filteredList.map((item) => (
            <ChecklistField.Option key={item.value} name={item.value} css={checklistOptionStyle}>
              {Label ? <Label item={item} /> : item.label}
              {!!item.locationName && <Chip.Location maxWidth='120px'>{item.locationName}</Chip.Location>}
            </ChecklistField.Option>
          ))}
        </ChecklistField>
      )}
    </section>
  );
};

const checklistOptionStyle = css({
  margin: 0,
  transition: 'background-color 0.2s ease-out',
  cursor: 'pointer',
  borderRadius: theme.borderRadius.small,
  alignItems: 'center',
  '&:hover': { backgroundColor: theme.colors.neutral5, '> div::before': { transform: 'scale(1)' } },
  '> label': {
    flex: 1,
    cursor: 'pointer',
    padding: theme.spacing(1),
    margin: 0,
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  '> div': { margin: theme.spacing(1, 0, 1, 1) },
});
